import React, { useEffect, useState, useRef } from 'react';
import { Col, Row, Form, Badge, OverlayTrigger, Tooltip, InputGroup, Button } from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { books_category } from '../../ProData';
import BookCover from '../../components/BookCover/BookCover';
import { parseKeywords, MAX_KEYWORDS } from '../../utils/keywordValidator';
import AsyncAuthorAutoComplete from '../../components/AutoComplete/AsyncAuthorAutoComplete';
import { fetchBookMetadataByIsbn } from '../../utils/isbnLookup';
import './BooksForm.css';

const config = require('../../config.json');

export default function BooksForm(props) {
  const {
    bookDetails,
    setBookDetails,
    viewOnly,
    formType,
    setNewAccessionNo,
    newAccessionNo,
    setImageFile,
    profileImage
  } = props;

  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef();
  const [loaded, setLoaded] = useState(true);
  const [isFetchingIsbn, setIsFetchingIsbn] = useState(false);
  const [isbnFeedback, setIsbnFeedback] = useState(null);
  const [isCheckingAccession, setIsCheckingAccession] = useState(false);
  const [accessionFeedback, setAccessionFeedback] = useState(null);

  const bookKeywordTags = parseKeywords(bookDetails?.bookKeywords);

  const handleIsbnLookup = async () => {
    if (!bookDetails.isbn || !bookDetails.isbn.trim() || bookDetails.isbn.toUpperCase() === 'NA') {
      setIsbnFeedback({ variant: 'warning', message: 'Please enter a valid ISBN number first.' });
      return;
    }
    try {
      setIsFetchingIsbn(true);
      setIsbnFeedback(null);
      const res = await fetchBookMetadataByIsbn(bookDetails.isbn);
      if (res.success && res.book) {
        setBookDetails((prev) => ({
          ...prev,
          bookTitle: res.book.bookTitle || prev.bookTitle,
          bookSubTitle: res.book.bookSubTitle || prev.bookSubTitle,
          bookAuthor: res.book.bookAuthor || prev.bookAuthor,
          bookPublisherName: res.book.bookPublisherName || prev.bookPublisherName,
          bookPublishYear: res.book.bookPublishYear || prev.bookPublishYear,
          bookPages: res.book.bookPages || prev.bookPages,
          bookKeywords: res.book.bookKeywords || prev.bookKeywords,
          bookAbstract: res.book.bookAbstract || prev.bookAbstract
        }));
        setIsbnFeedback({
          variant: 'success',
          message: `Loaded metadata from ${res.source}: "${res.book.bookTitle}" by ${res.book.bookAuthor}`
        });
      } else {
        setIsbnFeedback({
          variant: 'warning',
          message: res.error || 'No online bibliographic data found for this ISBN.'
        });
      }
    } catch (err) {
      console.error('ISBN lookup error:', err);
      setIsbnFeedback({
        variant: 'danger',
        message: 'Error fetching ISBN metadata. You can enter details manually.'
      });
    } finally {
      setIsFetchingIsbn(false);
    }
  };

  useEffect(() => {
    // Keep updated with profile image
  }, [profileImage]);

  // Validate manual accession number against backend (min 6 characters to reduce API calls)
  useEffect(() => {
    const accNo = (bookDetails?.accessionNo || '').trim().toUpperCase();
    if (bookDetails?.autoGenerate || !accNo || (formType !== "New" && formType !== "Clone")) {
      setAccessionFeedback(null);
      return;
    }

    // Minimum 6 characters required before dispatching backend validation call
    if (accNo.length < 6) {
      setAccessionFeedback({
        status: 'length_hint',
        message: `Min. 6 characters required (${accNo.length}/6)`
      });
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsCheckingAccession(true);
        setAccessionFeedback({ status: 'checking', message: `Checking accession #${accNo}...` });

        const { idToken } = (await fetchAuthSession()).tokens ?? {};
        const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/books`, {
          params: {
            fetchKey: accNo,
            fetchOption: 'AccessionNo',
            clientID: 'VJCollege'
          },
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });

        const data = Array.isArray(response.data) ? response.data : [];
        const match = data.find(
          (b) => (b.accessionNo || '').toString().trim().toUpperCase() === accNo
        );

        if (match) {
          setAccessionFeedback({
            status: 'exists',
            message: `Accession #${accNo} already exists in library catalogue!`,
            book: match
          });
          enqueueSnackbar(`Accession #${accNo} already exists! ("${match.bookTitle}")`, {
            variant: 'warning'
          });
        } else {
          setAccessionFeedback({
            status: 'available',
            message: `Accession #${accNo} is available for cataloguing.`,
            book: null
          });
        }
      } catch (err) {
        console.error('Error validating accession number:', err);
        setAccessionFeedback({
          status: 'error',
          message: 'Could not verify accession number availability.',
          book: null
        });
      } finally {
        setIsCheckingAccession(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [bookDetails?.accessionNo, bookDetails?.autoGenerate, formType, enqueueSnackbar]);

  const handleSelection = () => {
    setBookDetails({
      ...bookDetails,
      autoGenerate: !bookDetails.autoGenerate,
      accessionNo: ''
    });
    setAccessionFeedback(null);
  };

  const handleCategorySelection = (inputValue) => {
    let filteredValue = "OTHERS";
    if (inputValue && inputValue.length >= 3) {
      const tinputValue = parseInt(inputValue.substring(0, 3), 10);
      const tempFilter = books_category.filter(
        (item) => item.categoryID === tinputValue && item.categoryDisplay === "Yes"
      );
      if (tempFilter.length > 0) {
        filteredValue = tempFilter[0].name;
      }
    }
    setBookDetails({
      ...bookDetails,
      bookCategory: filteredValue,
      bookClassificationNo: inputValue
    });
  };

  const handleCategoryDropdownChange = (event) => {
    const selectedCat = event.target.value;
    const match = books_category.find((item) => item.name === selectedCat || item.categoryName === selectedCat);
    setBookDetails({
      ...bookDetails,
      bookCategory: selectedCat,
      bookClassificationNo: match?.classficationNo || bookDetails.bookClassificationNo
    });
  };

  const handleFileSelection = (event) => {
    setLoaded(false);
    event.preventDefault();
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setImageFile(file);
      const profile = document.getElementById("profile");
      if (profile) {
        profile.src = URL.createObjectURL(file);
      }
    }
    setLoaded(true);
  };

  const handleImageError = (e) => {
    e.target.src = "images/cat1.png";
  };

  const handleCopy = (text, label) => {
    if (text) {
      navigator.clipboard.writeText(String(text));
    }
  };

  // ================= VIEW ONLY PRESENTATION MODE =================
  if (viewOnly) {
    const shelfLoc = bookDetails.bookLocation || bookDetails.liblocation || bookDetails.location;
    const status = bookDetails.bookStatus || 'Active';
    const statusBg =
      status === 'Active'
        ? 'success'
        : status === 'Issued'
          ? 'primary'
          : status === 'Lost'
            ? 'danger'
            : status === 'Damaged'
              ? 'warning'
              : 'secondary';

    return (
      <div className="booksform-view-container">
        {/* Hero Header Card */}
        <div className="booksform-view-hero">
          <Row className="align-items-center g-4">
            <Col md="auto" className="text-center text-md-start">
              <BookCover
                title={bookDetails.bookTitle}
                author={bookDetails.bookAuthor}
                edition={bookDetails.bookEdition}
                category={bookDetails.bookCategory}
                callNumber={bookDetails.bookClassificationNo}
                accessionNo={bookDetails.autoGenerate ? 'Auto' : (bookDetails.accessionNo || '')}
                imageUrl={profileImage}
                size="lg"
                style={{ width: '140px', height: '190px' }}
              />
            </Col>
            <Col>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span className="badge bg-light text-dark px-3 py-2 font-monospace fs-6">
                  <i className="bi bi-upc me-1 text-primary"></i>
                  Accession #{bookDetails.autoGenerate ? 'Auto (Assigned on Save)' : (bookDetails.accessionNo || 'N/A')}
                </span>
                <Badge bg={statusBg} className="px-3 py-2 fs-6">
                  {status}
                </Badge>
                <span className="badge bg-info text-dark px-3 py-2 fs-6">
                  <i className="bi bi-tag me-1"></i>
                  {bookDetails.bookScope || 'Circulation'}
                </span>
              </div>

              <div className="booksform-view-title">{bookDetails.bookTitle || 'Untitled Book'}</div>
              {bookDetails.bookSubTitle && (
                <div className="booksform-view-subtitle">{bookDetails.bookSubTitle}</div>
              )}
              <div className="booksform-view-author">
                <i className="bi bi-person-fill me-1"></i> By {bookDetails.bookAuthor || 'Unknown Author'}
                {bookDetails.authorMark && bookDetails.authorMark !== 'NA' && (
                  <span className="text-white-50 ms-2 font-monospace">[{bookDetails.authorMark}]</span>
                )}
              </div>
            </Col>
          </Row>
        </div>

        {/* Key Metrics Cards */}
        <div className="booksform-view-grid">
          <div className="booksform-view-card">
            <div className="booksform-view-label">
              <i className="bi bi-bookmark-star text-primary"></i> Call Number
            </div>
            <div className="booksform-view-value font-monospace text-primary">
              {bookDetails.bookClassificationNo || 'N/A'}
            </div>
          </div>

          <div className="booksform-view-card">
            <div className="booksform-view-label">
              <i className="bi bi-folder2 text-success"></i> Category / Subject
            </div>
            <div className="booksform-view-value">
              {bookDetails.bookCategory || 'General'}
            </div>
          </div>

          <div className="booksform-view-card">
            <div className="booksform-view-label">
              <i className="bi bi-building text-warning"></i> Publisher
            </div>
            <div className="booksform-view-value">
              {bookDetails.bookPublisherName || 'N/A'}
              {bookDetails.bookPublishYear ? ` (${bookDetails.bookPublishYear})` : ''}
            </div>
          </div>

          <div className="booksform-view-card">
            <div className="booksform-view-label">
              <i className="bi bi-geo-alt-fill text-danger"></i> Shelf Location
            </div>
            <div className="booksform-view-value">
              {shelfLoc || 'Periodical / Main Stack'}
            </div>
          </div>

          <div className="booksform-view-card">
            <div className="booksform-view-label">
              <i className="bi bi-barcode text-secondary"></i> ISBN
            </div>
            <div className="booksform-view-value font-monospace">
              {bookDetails.isbn && bookDetails.isbn !== 'NA' ? bookDetails.isbn : 'Not Specified'}
            </div>
          </div>

          <div className="booksform-view-card">
            <div className="booksform-view-label">
              <i className="bi bi-files text-purple"></i> Edition & Copies
            </div>
            <div className="booksform-view-value">
              Ed: {bookDetails.bookEdition || '1st'} • {bookDetails.bookCopies || 1} {Number(bookDetails.bookCopies) === 1 ? 'Copy' : 'Copies'}
            </div>
          </div>

          <div className="booksform-view-card">
            <div className="booksform-view-label">
              <i className="bi bi-file-earmark-text text-info"></i> Pages & Price
            </div>
            <div className="booksform-view-value">
              {bookDetails.bookPages || '0'} Pages • ₹{bookDetails.bookPrice || '0'}
            </div>
          </div>

          <div className="booksform-view-card">
            <div className="booksform-view-label">
              <i className="bi bi-aspect-ratio text-dark"></i> Dimensions
            </div>
            <div className="booksform-view-value">
              {bookDetails.bookDimension && bookDetails.bookDimension !== 'NA' ? bookDetails.bookDimension : 'Standard'}
            </div>
          </div>
        </div>

        {/* Keywords & Abstract Section */}
        {bookDetails.bookKeywords && (
          <div className="booksform-section">
            <div className="booksform-section-title">
              <span className="booksform-section-icon"><i className="bi bi-tags"></i></span>
              Keywords & Subject Headings
            </div>
            <div className="d-flex flex-wrap gap-2">
              {bookDetails.bookKeywords.split(',').map((kw, i) => (
                <span key={i} className="badge bg-light text-dark border px-3 py-2">
                  #{kw.trim()}
                </span>
              ))}
            </div>
          </div>
        )}

        {bookDetails.bookAbstract && bookDetails.bookAbstract !== 'NA' && (
          <div className="booksform-section">
            <div className="booksform-section-title">
              <span className="booksform-section-icon"><i className="bi bi-card-text"></i></span>
              Book Abstract / Summary
            </div>
            <p className="text-secondary mb-0" style={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>
              {bookDetails.bookAbstract}
            </p>
          </div>
        )}
      </div>
    );
  }

  // ================= EDIT / NEW / CLONE / UPDATE MODE =================
  return (
    <div className="booksform-container">
      <Form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        {/* Mode: New / Clone Accession Switch */}
        {(formType === "New" || formType === "Clone") && (
          <div className="booksform-section bg-light border-primary border-opacity-25">
            <Row className="align-items-center g-3">
              <Col md={6}>
                <Form.Group id="accessionNo">
                  <Form.Check
                    type="switch"
                    id="auto-generate-switch"
                    label={<span className="fw-bold text-dark">Auto-generate Accession Number</span>}
                    checked={bookDetails.autoGenerate}
                    onChange={handleSelection}
                    className="fs-6"
                  />
                  <div className="small text-muted mt-1">
                    {bookDetails.autoGenerate
                      ? "System will assign the next sequential accession number automatically."
                      : "Manually assign a custom barcode or accession number below."}
                  </div>
                </Form.Group>
              </Col>
              {!bookDetails.autoGenerate && (
                <Col md={6}>
                  <Form.Group id="manualBookNo">
                    <Form.Label className="booksform-label d-flex justify-content-between align-items-center">
                      <span>
                        Manual Accession Number <span className="booksform-required">*</span>
                      </span>
                      {isCheckingAccession && (
                        <span className="text-muted small fw-normal d-flex align-items-center gap-1">
                          <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" style={{ width: '12px', height: '12px' }}></span>
                          Checking...
                        </span>
                      )}
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        required
                        type="text"
                        className={`booksform-input font-monospace ${accessionFeedback?.status === 'exists'
                          ? 'is-invalid border-danger'
                          : accessionFeedback?.status === 'available'
                            ? 'is-valid border-success'
                            : ''
                          }`}
                        placeholder="e.g. 158546"
                        value={bookDetails.accessionNo || ''}
                        onChange={(e) => setBookDetails({ ...bookDetails, accessionNo: e.target.value.toUpperCase() })}
                      />
                      {accessionFeedback?.status === 'exists' && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setBookDetails({ ...bookDetails, accessionNo: '' })}
                          title="Clear invalid accession number"
                        >
                          <i className="bi bi-x-lg"></i>
                        </Button>
                      )}
                    </InputGroup>

                    {/* Feedback Messages */}
                    {accessionFeedback && !isCheckingAccession && (
                      <div className="mt-1">
                        {accessionFeedback.status === 'length_hint' && (
                          <div className="text-muted small d-flex align-items-center gap-1 mt-1">
                            <i className="bi bi-info-circle text-secondary"></i>
                            <span>{accessionFeedback.message}</span>
                          </div>
                        )}
                        {accessionFeedback.status === 'exists' && (
                          <div className="text-danger small fw-semibold d-flex align-items-start gap-1 p-2 bg-danger bg-opacity-10 rounded border border-danger border-opacity-25 mt-1">
                            <i className="bi bi-exclamation-triangle-fill text-danger mt-1 flex-shrink-0"></i>
                            <div>
                              <div><strong>Accession #{bookDetails.accessionNo} already exists!</strong></div>
                              <div className="small text-dark opacity-75">
                                "{accessionFeedback.book?.bookTitle}" {accessionFeedback.book?.bookAuthor ? `by ${accessionFeedback.book.bookAuthor}` : ''}
                              </div>
                            </div>
                          </div>
                        )}
                        {accessionFeedback.status === 'available' && (
                          <div className="text-success small fw-semibold d-flex align-items-center gap-1 p-1 px-2 bg-success bg-opacity-10 rounded border border-success border-opacity-25 mt-1">
                            <i className="bi bi-check-circle-fill text-success flex-shrink-0"></i>
                            <span>Accession #{bookDetails.accessionNo} is available for cataloguing.</span>
                          </div>
                        )}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              )}
            </Row>
          </div>
        )}

        {/* Mode: Update Accession # Notice */}
        {formType === "Update" && (
          <div className="booksform-section bg-warning bg-opacity-10 border-warning">
            <div className="booksform-section-title text-dark">
              <span className="booksform-section-icon bg-warning text-dark"><i className="bi bi-upc-scan"></i></span>
              Re-assign Accession Number
            </div>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group id="currentAccession">
                  <Form.Label className="booksform-label">Current Accession #</Form.Label>
                  <Form.Control
                    type="text"
                    className="booksform-input font-monospace"
                    value={bookDetails.accessionNo}
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group id="newaccessionno">
                  <Form.Label className="booksform-label">
                    New Accession # <span className="booksform-required">*</span>
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    className="booksform-input font-monospace"
                    placeholder="Enter new accession number"
                    value={newAccessionNo}
                    onChange={(e) => setNewAccessionNo(e.target.value.toUpperCase())}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="small text-muted mt-2">
              <i className="bi bi-info-circle me-1 text-warning"></i>
              The new Accession # will replace the existing barcode across circulation and cataloguing records.
            </div>
          </div>
        )}

        {/* SECTION 1: Bibliographic Information */}
        <div className="booksform-section">
          <div className="booksform-section-title">
            <span className="booksform-section-icon"><i className="bi bi-book"></i></span>
            1. Bibliographic Information
          </div>
          <Row className="g-3">
            <Col md={7}>
              <Form.Group id="bookName">
                <Form.Label className="booksform-label">
                  Book Title <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  className="booksform-input"
                  placeholder="e.g. A Theology of the Old Testament"
                  value={bookDetails.bookTitle}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookTitle: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group id="authorName">
                <Form.Label className="booksform-label">
                  Author Name <span className="booksform-required">*</span>
                </Form.Label>
                <AsyncAuthorAutoComplete
                  required
                  value={bookDetails.bookAuthor}
                  onChange={(val) => setBookDetails({ ...bookDetails, bookAuthor: val })}
                  placeholder="Type author (e.g. Walter Eichrodt)..."
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group id="bookSubTitle">
                <Form.Label className="booksform-label">
                  Book Subtitle <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  className="booksform-input"
                  placeholder="e.g. Volume 1: God and the World (or NA if none)"
                  value={bookDetails.bookSubTitle}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookSubTitle: e.target.value })}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group id="bookClassificationNo">
                <Form.Label className="booksform-label">
                  Call Number (Classification) <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  className="booksform-input font-monospace"
                  placeholder="e.g. 230.04 EIC"
                  value={bookDetails.bookClassificationNo}
                  onChange={(e) => handleCategorySelection(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group id="bookCategory">
                <Form.Label className="booksform-label">
                  Book Category <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Select
                  className="booksform-select"
                  value={bookDetails.bookCategory}
                  onChange={handleCategoryDropdownChange}
                >
                  <option value="NA">Select Category</option>
                  {books_category
                    .filter((c) => c.categoryDisplay === "Yes")
                    .map((c) => (
                      <option key={c.categoryID || c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/*<Col md={4}>
              <Form.Group id="authorMark">
                <Form.Label className="booksform-label">
                  Author Mark / Cutter <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  className="booksform-input font-monospace"
                  placeholder="e.g. EIC or NA"
                  value={bookDetails.authorMark}
                  onChange={(e) => setBookDetails({ ...bookDetails, authorMark: e.target.value.toUpperCase() })}
                />
              </Form.Group>
            </Col>*/}
          </Row>
        </div>

        {/* SECTION 2: Publication & Classification */}
        <div className="booksform-section">
          <div className="booksform-section-title">
            <span className="booksform-section-icon"><i className="bi bi-building"></i></span>
            2. Publication & Classification
          </div>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group id="publisherName">
                <Form.Label className="booksform-label">
                  Publisher Name <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  className="booksform-input"
                  placeholder="e.g. SCM Press"
                  value={bookDetails.bookPublisherName}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookPublisherName: e.target.value })}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group id="publisherLocation">
                <Form.Label className="booksform-label">
                  Publisher Location <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  className="booksform-input"
                  placeholder="e.g. London"
                  value={bookDetails.bookPublisherLocation}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookPublisherLocation: e.target.value })}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group id="publishYear">
                <Form.Label className="booksform-label">
                  Publish Year <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  className="booksform-input"
                  placeholder="e.g. 2022"
                  value={bookDetails.bookPublishYear}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookPublishYear: e.target.value })}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group id="isbn">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label className="booksform-label mb-0">
                    ISBN Number
                  </Form.Label>
                  {bookDetails.isbn && bookDetails.isbn.trim() !== '' && bookDetails.isbn.toUpperCase() !== 'NA' && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none fw-bold"
                      style={{ fontSize: '0.78rem', color: '#0284c7' }}
                      onClick={handleIsbnLookup}
                      disabled={isFetchingIsbn}
                      title="Auto-fill book title, author, publisher and pages from ISBN"
                    >
                      {isFetchingIsbn ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Fetching...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-magic me-1"></i> Auto-Fill
                        </>
                      )}
                    </button>
                  )}
                </div>
                <InputGroup>
                  <Form.Control
                    type="text"
                    className="booksform-input font-monospace"
                    placeholder="e.g. 978-0-334-01646-4 (or NA)"
                    value={bookDetails.isbn}
                    onChange={(e) => {
                      setBookDetails({ ...bookDetails, isbn: e.target.value.toUpperCase() });
                      setIsbnFeedback(null);
                    }}
                  />
                  <Button
                    variant="outline-secondary"
                    style={{ borderColor: '#cbd5e1' }}
                    onClick={handleIsbnLookup}
                    disabled={isFetchingIsbn || !bookDetails.isbn || bookDetails.isbn === 'NA'}
                    title="Fetch book details via ISBN"
                  >
                    <i className={`bi ${isFetchingIsbn ? 'bi-hourglass-split' : 'bi-search'}`}></i>
                  </Button>
                </InputGroup>
                {isbnFeedback && (
                  <div
                    className={`alert alert-${isbnFeedback.variant} p-1 px-2 mt-1 mb-0 small`}
                    style={{ fontSize: '0.76rem', borderRadius: '6px' }}
                  >
                    <i className={`bi bi-${isbnFeedback.variant === 'success' ? 'check-circle-fill' : 'exclamation-triangle-fill'} me-1`}></i>
                    {isbnFeedback.message}
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group id="bookEdition">
                <Form.Label className="booksform-label">
                  Edition
                </Form.Label>
                <Form.Control
                  type="text"
                  className="booksform-input"
                  placeholder="e.g. 2nd Rev. Ed. (or NA)"
                  value={bookDetails.bookEdition}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookEdition: e.target.value })}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group id="bookDimension">
                <Form.Label className="booksform-label">
                  Dimension / Format
                </Form.Label>
                <Form.Control
                  type="text"
                  className="booksform-input"
                  placeholder="e.g. 24 cm (or NA)"
                  value={bookDetails.bookDimension}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookDimension: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* SECTION 3: Physical & Library Details */}
        <div className="booksform-section">
          <div className="booksform-section-title">
            <span className="booksform-section-icon"><i className="bi bi-geo-alt"></i></span>
            3. Library Location & Circulation Status
          </div>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group id="bookLocation">
                <Form.Label className="booksform-label">
                  Shelf Location
                </Form.Label>
                <Form.Control
                  type="text"
                  className="booksform-input"
                  placeholder="e.g. Stack 3, Rack B-4"
                  value={bookDetails.bookLocation}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookLocation: e.target.value })}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group id="bookScope">
                <Form.Label className="booksform-label">
                  Book Scope <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Select
                  className="booksform-select"
                  value={bookDetails.bookScope}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookScope: e.target.value })}
                >
                  <option value="Circulation">Circulation (Standard Loan)</option>
                  <option value="Reference">Reference (1-Day Student Loan / 90-Day Staff Loan)</option>
                  <option value="E-Books">E-Books / Digital</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group id="bookStatus">
                <Form.Label className="booksform-label">
                  Book Status <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Select
                  className="booksform-select"
                  value={bookDetails.bookStatus}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookStatus: e.target.value })}
                >
                  <option value="Active">Active (Available)</option>
                  <option value="In Active">In Active</option>
                  <option value="Issued">Issued</option>
                  <option value="Lost">Lost</option>
                  <option value="Damaged">Damaged</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group id="bookCopies">
                <Form.Label className="booksform-label">
                  Copies to Add <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="number"
                  min={1}
                  max={10}
                  className="booksform-input"
                  value={bookDetails.bookCopies}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookCopies: Number(e.target.value) })}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group id="bookPrice">
                <Form.Label className="booksform-label">
                  Book Price (₹) <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  className="booksform-input"
                  placeholder="e.g. 750"
                  value={bookDetails.bookPrice}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookPrice: e.target.value })}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group id="bookPages">
                <Form.Label className="booksform-label">
                  Book Pages <span className="booksform-required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  className="booksform-input"
                  placeholder="e.g. 480"
                  value={bookDetails.bookPages}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookPages: e.target.value })}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* SECTION 4: Discovery & Cover Poster */}
        <div className="booksform-section">
          <div className="booksform-section-title">
            <span className="booksform-section-icon"><i className="bi bi-image"></i></span>
            4. Discovery & Cover Poster
          </div>
          <Row className="g-3">
            <Col md={8}>
              <Form.Group id="bookKeywords" className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label className="booksform-label mb-0">
                    Book Keywords <span className="booksform-required">*</span>
                  </Form.Label>
                  <span className={`badge ${bookKeywordTags.length > MAX_KEYWORDS ? 'bg-danger text-white' : bookKeywordTags.length >= 8 ? 'bg-warning text-dark' : 'bg-light text-dark border'}`}>
                    {bookKeywordTags.length} / {MAX_KEYWORDS} Max
                  </span>
                </div>
                <Form.Control
                  required
                  type="text"
                  className="booksform-input"
                  placeholder="e.g. OLD TESTAMENT, SYSTEMATIC THEOLOGY, BIBLICAL STUDIES (comma-separated)"
                  value={bookDetails.bookKeywords || ''}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookKeywords: e.target.value.toUpperCase() })}
                />
                <div className="small text-muted mt-1">
                  <i className="bi bi-info-circle me-1 text-primary"></i>
                  Up to {MAX_KEYWORDS} concise terms separated by commas. Place paragraphs in <strong>Book Abstract</strong> below.
                </div>

                {bookKeywordTags.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 align-items-center mt-2">
                    {bookKeywordTags.map((tag, idx) => {
                      const isTooLong = tag.length > 40 || tag.split(/\s+/).length > 5;
                      return (
                        <span
                          key={idx}
                          className={`badge ${isTooLong ? 'bg-danger text-white' : 'bg-light text-dark border'} px-2 py-1`}
                          style={{ fontSize: '0.78rem' }}
                        >
                          <i className={`bi ${isTooLong ? 'bi-exclamation-triangle-fill me-1' : 'bi-tag me-1 text-primary'}`}></i>
                          {tag}
                          {isTooLong && <span className="ms-1 fw-bold">(Too long)</span>}
                        </span>
                      );
                    })}
                  </div>
                )}
              </Form.Group>

              <Form.Group id="bookAbstract">
                <Form.Label className="booksform-label">
                  Book Abstract / Summary
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  className="booksform-input"
                  placeholder="Enter a brief description or summary of the book content..."
                  value={bookDetails.bookAbstract}
                  onChange={(e) => setBookDetails({ ...bookDetails, bookAbstract: e.target.value })}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <div className="booksform-poster-card">
                <div className="small fw-bold text-muted mb-2">Book Cover Preview</div>
                <div className="d-flex justify-content-center mb-2">
                  <BookCover
                    title={bookDetails.bookTitle || "Book Title"}
                    author={bookDetails.bookAuthor || "Author Name"}
                    edition={bookDetails.bookEdition}
                    category={bookDetails.bookCategory}
                    callNumber={bookDetails.bookClassificationNo}
                    accessionNo={bookDetails.autoGenerate ? 'Auto' : (bookDetails.accessionNo || '')}
                    imageUrl={profileImage}
                    size="md"
                    style={{ width: '120px', height: '165px' }}
                  />
                </div>
                <div className="booksform-img-upload mt-2">
                  <label htmlFor="book-image-upload" className="booksform-upload-btn">
                    <i className="bi bi-cloud-arrow-up me-1"></i> Upload Cover
                  </label>
                  <input
                    id="book-image-upload"
                    type="file"
                    accept="image/*"
                    ref={inputRef}
                    onChange={handleFileSelection}
                  />
                </div>
                <div className="small text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                  JPEG / PNG format up to 5MB
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Form>
    </div>
  );
}
